import React, { useState, useEffect, useCallback, useRef } from 'react';
import { hasPermission, PERMISSIONS } from '../../utils/permissions';
import ScreenshotCapture from './ScreenshotCapture';
// 需求 R129 R3（PM 2026-08-21 追問 Q6：「若只是新增商品，與欄位必填的關聯性為何？」）
//
// 查證結論（證據見 tests/productModalReachability.fixture.js）：
// 本彈窗**目前在畫面上進不來**——ProductsPage.js:1309 的「新增產品」按鈕是
// navigate('/products/new')，走 App.js:25 路由到 ProductDetailPage（isCreate=true，
// ProductDetailPage.js:248/616 POST /api/products）；ProductsPage.js:318 的 openAdd()
// 是唯一會 setModalOpen(true) 的函式，而它全專案零呼叫端，因此 open 永遠是 false，
// 本檔 `if (!open) return null` 永遠成立。
// ⇒「新增商品」的必填驗證在 R1（commit 2bfcb56）就已經隨 ProductDetailPage 做完了。
//
// 那本輪為什麼還是改這支？因為必填定義若只有詳情頁遵守，這支殘留的舊表單一旦被重新接上
// （openAdd 補個呼叫端就活了）就會變成繞過必填的後門。故本輪讓它與詳情頁共用同一份
// PRODUCT_REQUIRED_FIELDS（AC14「兩處共用同一份必填定義」），不另寫第二組清單。
import {
  PRODUCT_REQUIRED_FIELDS,
  findMissingRequiredFields,
  isRequiredFieldFilled,
  requiredFieldMessage,
} from '../../utils/productRequiredFields';

const API = process.env.REACT_APP_API_URL || '/api';

// 需求 R129 R3：七個必填欄位分別落在彈窗的哪一個分頁（跳至第一個未填欄位時要先切分頁）。
// key 一律取自 PRODUCT_REQUIRED_FIELDS，不在此另立欄位清單；fixture 會斷言兩者的 key 一致。
const REQUIRED_FIELD_TAB = {
  productName: '產品基本資訊',
  spec: '產品基本資訊',
  pcsPerBox: '產品基本資訊',
  shelfLife: '產品基本資訊',
  netWeight: '物流與物理屬性',
  grossWeight: '物流與物理屬性',
  volumeCbm: '物流與物理屬性',
};

// Helpers
const emptyForm = () => ({
  systemCode: '',
  productCode: '',
  productName: '',
  englishName: '',
  series: '',
  category: '',
  subCategory: '',
  tags: [],
  brand: '',
  supplier: '',
  origin: '',
  spec: '',
  packingUnit: '',
  pcsPerBox: '',
  barcode: '',
  productImage: '',
  vegetarianType: '',
  shelfLife: '',
  retailPrice: '',
  sealingColor: '',
  salesNotes: '',
  lastUpdatedDate: '',
  productNotes: '',
  hkExcluded: false,
  // Logistics
  netWeight: '',
  grossWeight: '',
  boxLength: '',
  boxWidth: '',
  boxHeight: '',
  volumeMethod: 'auto',
  volumeCuft: '',
  volumeCbm: '',
  // Cost
  costSource: 'factory',
  factoryCost: { supplierName: '', boxPrice: '', unitPrice: '', moq: '', notes: '', shippableCountries: '', nonShippableCountries: '' },
  distributorCosts: [],
  // HK
  hkMarkup: true,
  hkReferencePrice: '',
  hkShippingCostPerPack: '',
  hkShippingCostPerBox: '',
  hkTaiwanPrice: '',
  hkLogisticsCost: '',
  hkShippingFee: '',
  hkInboundCost: '',
  // Marketing
  productStatus: 'listed',
  catalogStatus: 'in_catalog',
});

const emptyDistributor = () => ({ boxPrice: '', unitPrice: '', moq: '', notes: '', shippableCountries: '', nonShippableCountries: '' });

// Unit price: ceil to 1 decimal
// 需求 R140 Q6=A（PM 2026-08-28：「Q6：A＝補防線（三處同步）」）：
// 原本是裸的 Math.ceil(bp / pcs * 10) / 10，沒有浮點雜訊防線 —— 2.1 ÷ 3 在 JS 裡是
// 0.7000000000000001，×10 後比 7 多一丁點，Math.ceil 就把它推成 0.8（憑空多 0.1）。
// 同檔 fmtMoney（需求 R70）與 server/utils/cost.js ceilToOneDecimal 一直都有這道
// Number((v * 10).toFixed(6)) 防線，只有本函式漏掉；三處必須同時補，否則會出現
// 「匯入算 0.7、手動編輯算 0.8」的新漂移（server 端同一輪已改 cost.js calcUnitPrice）。
// 實例：2.1÷3 0.8→0.7、8.4÷3 2.9→2.8、10.8÷36 0.4→0.3。窮舉 480,000 組受影響 0.5196%，
// 方向一律是舊寫法多 0.1。需求 R70「1 位無條件進位」規則本身不變。
const calcUnitPrice = (boxPrice, pcsPerBox) => {
  const bp = parseFloat(boxPrice);
  const pcs = parseFloat(pcsPerBox);
  if (!bp || !pcs || pcs === 0) return '';
  const scaled = Number((bp / pcs * 10).toFixed(6));
  return (Math.ceil(scaled) / 10).toFixed(1);
};

// CUFT: (L*W*H)/28317, round to 2 decimal
const calcCuft = (l, w, h) => {
  const L = parseFloat(l), W = parseFloat(w), H = parseFloat(h);
  if (!L || !W || !H) return '';
  return (Math.round((L * W * H / 28317) * 100) / 100).toFixed(2);
};

// CBM: Cuft/35.315, round to 3 decimal
const calcCbm = (cuft) => {
  const c = parseFloat(cuft);
  if (!c) return '';
  return (Math.round((c / 35.315) * 1000) / 1000).toFixed(3);
};

// Ceil to 1 decimal (無條件進位至小數第一位)
const ceil1 = (n) => Math.ceil(n * 10) / 10;

// HK calculations (per spec: 全部欄位顯示到小數第一位，無條件進位)
const calcHK = (form, hkdRate) => {
  if (!hkdRate) return {};
  const rate = parseFloat(hkdRate);
  if (!rate) return {};

  const pcs = parseFloat(form.pcsPerBox) || 0;
  const cuft = parseFloat(form.volumeCuft) || 0;

  // Get selected box price
  let boxPrice = 0;
  if (form.costSource === 'factory') {
    boxPrice = parseFloat(form.factoryCost?.boxPrice) || 0;
  } else if (form.costSource?.startsWith('distributor_')) {
    const idx = parseInt(form.costSource.split('_')[1], 10);
    boxPrice = parseFloat(form.distributorCosts?.[idx]?.boxPrice) || 0;
  } else if (form.costSource === 'distributor' && form.distributorCosts?.length > 0) {
    // Legacy fallback for existing data stored as 'distributor'
    boxPrice = parseFloat(form.distributorCosts[0]?.boxPrice) || 0;
  }

  if (!boxPrice || !rate) return {};

  // 台灣進價(箱): boxPrice / rate, 勾選加價時 boxPrice * 1.05 / rate
  const hkTaiwanPrice = form.hkMarkup
    ? ceil1(boxPrice * 1.05 / rate)
    : ceil1(boxPrice / rate);

  // 船務成本: 10 × CUFT
  const hkShippingFee = cuft ? ceil1(10 * cuft) : 0;

  // 進倉成本: 台灣進價 + 船務成本（需求 R50，PM 2026-06-17 Q3=A 維持原物流/出貨公式不改）
  const hkInboundCost = ceil1(hkTaiwanPrice + hkShippingFee);

  // 物流成本: (船務成本 + 台灣進價) × 0.035，上限 15
  const hkLogisticsCost = Math.min(ceil1((hkShippingFee + hkTaiwanPrice) * 0.035), 15);

  // 出貨成本(箱): 物流成本 + 船務成本 + 台灣進價
  const hkShippingCostPerBox = ceil1(hkLogisticsCost + hkShippingFee + hkTaiwanPrice);

  // 出貨成本(包): 出貨成本(箱) / 箱入數
  const hkShippingCostPerPack = pcs ? ceil1(hkShippingCostPerBox / pcs) : 0;

  // 參考價格: 出貨成本(箱) / 0.75
  const hkReferencePrice = ceil1(hkShippingCostPerBox / 0.75);

  return { hkTaiwanPrice, hkShippingFee, hkInboundCost, hkLogisticsCost, hkShippingCostPerBox, hkShippingCostPerPack, hkReferencePrice };
};

// 需求 R70（PM 2026-07-19 拍板）：金額欄位一律「最多到小數點第一位、
// 無條件進位」，顯示固定一位小數（整數補 .0，例：350 → 350.0；813.7499999999999 → 813.8；
// 32.60 → 32.6）。先以 toFixed(6) 清浮點雜訊再 ×10 無條件進位、÷10 還原，最後 toFixed(1)。
const fmtMoney = (v) => {
  if (v === '' || v === null || v === undefined) return '';
  const n = parseFloat(v);
  if (isNaN(n)) return '';
  const scaled = Number((n * 10).toFixed(6));
  return (Math.ceil(scaled) / 10).toFixed(1);
};

// Option Manager sub-component
const OptionManager = ({ type, label, options, onOptionsChange, canEdit }) => {
  const [newValue, setNewValue] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState('');

  const token = () => localStorage.getItem('token');

  const addOption = async () => {
    if (!newValue.trim()) return;
    try {
      const res = await fetch(`${API}/products/options`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ type, value: newValue.trim() }),
      });
      if (res.ok) {
        const created = await res.json();
        onOptionsChange([...options, created]);
        setNewValue('');
      }
    } catch (err) { console.error(err); }
  };

  const saveEdit = async (id) => {
    if (!editingValue.trim()) return;
    try {
      const res = await fetch(`${API}/products/options/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ value: editingValue.trim() }),
      });
      if (res.ok) {
        const updated = await res.json();
        onOptionsChange(options.map((o) => (o._id === id ? updated : o)));
        setEditingId(null);
      }
    } catch (err) { console.error(err); }
  };

  const deleteOption = async (id) => {
    try {
      const res = await fetch(`${API}/products/options/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (res.ok) {
        onOptionsChange(options.filter((o) => o._id !== id));
      } else {
        const data = await res.json();
        alert(data.message || '刪除失敗');
      }
    } catch (err) { console.error(err); }
  };

  return (
    <div className="option-manager-panel">
      <div className="option-manager-header">
        <span>管理「{label}」選項</span>
        <span style={{ fontSize: '11px' }}>{options.length} 個選項</span>
      </div>
      <div style={{ maxHeight: 140, overflowY: 'auto' }}>
        {options.map((o) => (
          <div key={o._id} className="option-item">
            {editingId === o._id ? (
              <>
                <input
                  className="option-item-input"
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(o._id); if (e.key === 'Escape') setEditingId(null); }}
                  autoFocus
                />
                <button className="option-item-btn save-btn" onClick={() => saveEdit(o._id)}>✓</button>
                <button className="option-item-btn cancel-btn" onClick={() => setEditingId(null)}>✕</button>
              </>
            ) : (
              <>
                <span className="option-item-value">{o.value}</span>
                {canEdit && (
                  <>
                    <button className="option-item-btn edit-btn" onClick={() => { setEditingId(o._id); setEditingValue(o.value); }}>編輯</button>
                    <button className="option-item-btn delete-btn" onClick={() => deleteOption(o._id)}>刪除</button>
                  </>
                )}
              </>
            )}
          </div>
        ))}
        {options.length === 0 && <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', padding: '4px 0' }}>尚無選項</div>}
      </div>
      {canEdit && (
        <div className="option-add-row">
          <input
            className="option-add-input"
            placeholder="輸入新選項..."
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addOption(); }}
          />
          <button className="option-add-btn" onClick={addOption}>+ 新增</button>
        </div>
      )}
    </div>
  );
};

// Main Modal
const ProductModal = ({ open, mode, product, currentUser, onClose, onSave, onDelete, onModeChange }) => {
  const [form, setForm] = useState(emptyForm());
  const [currentMode, setCurrentMode] = useState(mode);
  const [activeTab, setActiveTab] = useState(0);

  // Options state
  const [seriesOptions, setSeriesOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  // 需求 R59 D3(A) / D6(B)：葷素備註改純文字輸入，移除 vegetarianOptions 狀態與 fetch。

  // Which option managers are open
  const [openManagers, setOpenManagers] = useState({});

  // Exchange rate
  const [hkdRate, setHkdRate] = useState(null);

  // Full product data (for view/edit - fetched with all fields)
  const [fullProduct, setFullProduct] = useState(null);

  // 需求 R129 R3：未通過驗證的必填欄位 key（依 PRODUCT_REQUIRED_FIELDS 的畫面順序），
  // 驅動紅框＋欄位下方紅字（Q4=B）。彈窗有分頁，另存「要跳去的欄位」等分頁切好再捲。
  const [missingKeys, setMissingKeys] = useState([]);
  const [pendingJumpKey, setPendingJumpKey] = useState(null);
  const requiredBoxRefs = useRef({});
  const requiredInputRefs = useRef({});
  const modalBodyRef = useRef(null);

  useEffect(() => { setCurrentMode(mode); }, [mode]);

  const fetchOptions = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(`${API}/products/options`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const all = await res.json();
        setSeriesOptions(all.filter((o) => o.type === 'series'));
        setCategoryOptions(all.filter((o) => o.type === 'category'));
        setSubCategoryOptions(all.filter((o) => o.type === 'subCategory'));
        setTagOptions(all.filter((o) => o.type === 'tag'));
        // 需求 R59：vegetarianType 已改純文字輸入，不再取用選項清單。
      }
    } catch (err) { console.error(err); }
  }, []);

  const fetchExchangeRate = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(`${API}/exchange-rates`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const rates = await res.json();
        const hk = rates.find((r) => r.key === 'hkd_internal');
        if (hk) setHkdRate(hk.rate);
      }
    } catch (err) { console.error(err); }
  }, []);

  const fetchFullProduct = useCallback(async (id) => {
    const token = localStorage.getItem('token');
    if (!token || !id) return;
    try {
      const res = await fetch(`${API}/products/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setFullProduct(data);
        return data;
      }
    } catch (err) { console.error(err); }
    return null;
  }, []);

  useEffect(() => {
    if (open) {
      setActiveTab(0);
      setOpenManagers({});
      // 需求 R129 R3：每次重新開啟彈窗都清掉上一輪的紅框／待跳欄位，避免殘留。
      setMissingKeys([]);
      setPendingJumpKey(null);
      fetchOptions();
      fetchExchangeRate();

      if (product && (mode === 'view' || mode === 'edit')) {
        fetchFullProduct(product._id).then((data) => {
          if (data) populateForm(data);
        });
      } else {
        setFullProduct(null);
        setForm(emptyForm());
      }
    }
  }, [open, product, mode]); // eslint-disable-line

  const populateForm = (data) => {
    setForm({
      systemCode: data.systemCode || '',
      productCode: data.productCode || '',
      productName: data.productName || '',
      englishName: data.englishName || '',
      series: data.series || '',
      category: data.category || '',
      subCategory: data.subCategory || '',
      tags: data.tags || [],
      brand: data.brand || '',
      supplier: data.supplier || '',
      origin: data.origin || '',
      spec: data.spec || '',
      packingUnit: data.packingUnit || '',
      pcsPerBox: data.pcsPerBox ?? '',
      barcode: data.barcode || '',
      productImage: data.productImage || '',
      vegetarianType: data.vegetarianType || '',
      shelfLife: data.shelfLife || '',
      retailPrice: data.retailPrice ?? '',
      sealingColor: data.sealingColor || '',
      hkExcluded: data.hkExcluded || false,
      netWeight: data.netWeight ?? '',
      grossWeight: data.grossWeight ?? '',
      boxLength: data.boxLength ?? '',
      boxWidth: data.boxWidth ?? '',
      boxHeight: data.boxHeight ?? '',
      volumeMethod: data.volumeMethod || 'auto',
      volumeCuft: data.volumeCuft ?? '',
      volumeCbm: data.volumeCbm ?? '',
      costSource: data.costSource === 'distributor' ? 'distributor_0' : (data.costSource || 'factory'),
      factoryCost: {
        supplierName: data.factoryCost?.supplierName || '',
        // 需求 R70：既有資料箱價載入即以 fmtMoney 正規化（無條件進位至一位），顯示乾淨、存檔正確。
        boxPrice: fmtMoney(data.factoryCost?.boxPrice ?? ''),
        unitPrice: data.factoryCost?.unitPrice ?? '',
        moq: data.factoryCost?.moq || '',
        notes: data.factoryCost?.notes || '',
        shippableCountries: data.factoryCost?.shippableCountries || '',
        nonShippableCountries: data.factoryCost?.nonShippableCountries || '',
      },
      distributorCosts: (data.distributorCosts || []).map((d) => ({
        supplierName: d.supplierName || '',
        boxPrice: fmtMoney(d.boxPrice ?? ''), // 需求 R70：同工廠成本，載入即正規化
        unitPrice: d.unitPrice ?? '',
        moq: d.moq || '',
        notes: d.notes || '',
        shippableCountries: d.shippableCountries || '',
        nonShippableCountries: d.nonShippableCountries || '',
      })),
      hkMarkup: data.hkMarkup ?? true,
      hkReferencePrice: data.hkReferencePrice ?? '',
      hkShippingCostPerPack: data.hkShippingCostPerPack ?? '',
      hkShippingCostPerBox: data.hkShippingCostPerBox ?? '',
      hkTaiwanPrice: data.hkTaiwanPrice ?? '',
      hkLogisticsCost: data.hkLogisticsCost ?? '',
      hkShippingFee: data.hkShippingFee ?? '',
      hkInboundCost: data.hkInboundCost ?? '',
      productStatus: data.productStatus || 'listed',
      catalogStatus: data.catalogStatus || 'in_catalog',
    });
  };

  // Auto-calculate CUFT/CBM when auto mode or dimensions change
  useEffect(() => {
    if (form.volumeMethod === 'auto') {
      const cuft = calcCuft(form.boxLength, form.boxWidth, form.boxHeight);
      const cbm = calcCbm(cuft);
      setForm((prev) => ({ ...prev, volumeCuft: cuft, volumeCbm: cbm }));
    }
  }, [form.volumeMethod, form.boxLength, form.boxWidth, form.boxHeight]);

  // Auto-calculate CBM when Cuft changes (supplier/estimate mode)
  // 需求 R129 R4（2026-08-21）：與 ProductDetailPage 同一個洞——原本多一道
  // `&& form.volumeCuft !== ''`，CUFT 被清空時不重算，volumeCbm 留著舊值，
  // 在 Q2=A 之下變成必填驗證的繞道。改為無條件重算（calcCbm('') 回 ''）。
  useEffect(() => {
    if (form.volumeMethod !== 'auto') {
      const cbm = calcCbm(form.volumeCuft);
      setForm((prev) => (prev.volumeCbm === cbm ? prev : { ...prev, volumeCbm: cbm }));
    }
  }, [form.volumeMethod, form.volumeCuft]);

  // Auto-calculate factory unit price
  useEffect(() => {
    const up = calcUnitPrice(form.factoryCost?.boxPrice, form.pcsPerBox);
    setForm((prev) => ({
      ...prev,
      factoryCost: { ...prev.factoryCost, unitPrice: up },
    }));
  }, [form.factoryCost?.boxPrice, form.pcsPerBox]);

  // Auto-calculate distributor unit prices
  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      distributorCosts: prev.distributorCosts.map((d) => ({
        ...d,
        unitPrice: calcUnitPrice(d.boxPrice, prev.pcsPerBox),
      })),
    }));
  }, [form.pcsPerBox]); // eslint-disable-line

  const isView = currentMode === 'view';
  const role = currentUser?.role;

  const canBasicEdit = hasPermission(role, PERMISSIONS.PRODUCT_BASIC_EDIT);
  const canLogisticsEdit = hasPermission(role, PERMISSIONS.PRODUCT_LOGISTICS_EDIT);
  const canCostView = hasPermission(role, PERMISSIONS.PRODUCT_COST_VIEW);
  const canHKView = hasPermission(role, PERMISSIONS.PRODUCT_HK_VIEW);
  const canMarketingEdit = hasPermission(role, PERMISSIONS.PRODUCT_MARKETING_EDIT);
  // 需求 R4（PM 2026-05-08 補充指示）：
  // 採購可編輯產品狀態（上架/下架），但目錄/官網狀態仍只有主管可改。
  const canProductStatusEdit = hasPermission(role, PERMISSIONS.PRODUCT_STATUS_EDIT);
  // 需求 R157（PM 2026-09-01 拍板 Q1=A/Q2=A/Q3=A/Q4=A）：稽核紀錄開放
  // manager／purchasing／sales 三個角色。**推翻** 需求 R146 時代的 manager-only 現況。
  // 本行與 TABS 的 { label: '稽核紀錄', visible: canAuditView } 都不必動——放開的是
  // permissions.js 的角色陣列；禁止改寫死 true 或 role 字串比對（Q2=A，fixture 會紅）。
  // 採購／業務看到的欄位與主管完全一致（Q3=A），renderAudit 不做角色分流。
  const canAuditView = hasPermission(role, PERMISSIONS.PRODUCT_AUDIT_VIEW);

  // HK computed values
  const hkValues = canHKView ? calcHK(form, hkdRate) : {};

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  // ── 需求 R129 R3：必填欄位驗證（與 ProductDetailPage 同一份定義、同一套 Q1-Q9 決議）──
  // TABS 提前到這裡宣告，是因為「跳到第一個未填欄位」必須先把該欄位所在的分頁切出來，
  // 而切分頁需要知道分頁在 visibleTabs 裡的 index（權限不同，index 會位移）。
  const TABS = [
    { label: '產品基本資訊', visible: true },
    { label: '物流與物理屬性', visible: true },
    { label: '成本與階梯價格', visible: canCostView },
    { label: '香港專用欄位', visible: canHKView },
    { label: '行銷', visible: true },
    { label: '稽核紀錄', visible: canAuditView },
  ];
  const visibleTabs = TABS.filter((t) => t.visible);

  const registerRequiredBox = (key) => (el) => { requiredBoxRefs.current[key] = el; };
  const registerRequiredInput = (key) => (el) => { requiredInputRefs.current[key] = el; };
  const isMissing = (key) => missingKeys.includes(key);
  const requiredClass = (key, base = 'form-control') => `${base}${isMissing(key) ? ' pd-field-invalid' : ''}`;
  const renderRequiredError = (key) => {
    if (!isMissing(key)) return null;
    const field = PRODUCT_REQUIRED_FIELDS.find((f) => f.key === key);
    return <div className="pd-field-error-msg">{requiredFieldMessage(field ? field.label : '此欄位')}</div>;
  };

  // 第一個未填欄位可能不在當前分頁：先把分頁切過去，等 DOM 換好（pendingJumpKey 觸發下一輪
  // effect）再捲動＋focus。捲動容器是 .modal-body（彈窗自己的捲軸），不是 window。
  const jumpToRequiredField = (key) => {
    const tabLabel = REQUIRED_FIELD_TAB[key];
    const idx = visibleTabs.findIndex((t) => t.label === tabLabel);
    if (idx >= 0 && idx !== activeTab) setActiveTab(idx);
    setPendingJumpKey(key);
  };

  useEffect(() => {
    if (!pendingJumpKey) return;
    const key = pendingJumpKey;
    const box = requiredBoxRefs.current[key];
    const input = requiredInputRefs.current[key];
    const body = modalBodyRef.current;
    if (box && body) {
      const GAP = 12;
      const top = body.scrollTop + box.getBoundingClientRect().top - body.getBoundingClientRect().top - GAP;
      body.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
    }
    if (input && typeof input.focus === 'function') {
      try { input.focus({ preventScroll: true }); } catch (err) { input.focus(); }
    }
    setPendingJumpKey(null);
  }, [pendingJumpKey, activeTab]);

  // AC10：使用者一補上內容，紅框／紅字立刻解除，不必重按儲存。
  useEffect(() => {
    setMissingKeys((prev) => {
      if (!prev.length) return prev;
      const next = prev.filter((k) => !isRequiredFieldFilled(k, form[k]));
      return next.length === prev.length ? prev : next;
    });
  }, [form]);

  const updateFactoryCost = (field, value) => {
    setForm((prev) => ({ ...prev, factoryCost: { ...prev.factoryCost, [field]: value } }));
  };

  const updateDistributorCost = (idx, field, value) => {
    setForm((prev) => {
      const costs = [...prev.distributorCosts];
      costs[idx] = { ...costs[idx], [field]: value };
      if (field === 'boxPrice') {
        costs[idx].unitPrice = calcUnitPrice(value, prev.pcsPerBox);
      }
      return { ...prev, distributorCosts: costs };
    });
  };

  const addDistributor = () => {
    if (form.distributorCosts.length >= 3) return;
    setForm((prev) => ({ ...prev, distributorCosts: [...prev.distributorCosts, emptyDistributor()] }));
  };

  const removeDistributor = (idx) => {
    setForm((prev) => {
      const newCosts = prev.distributorCosts.filter((_, i) => i !== idx);
      let newCostSource = prev.costSource;
      // If the removed distributor was the selected cost source, fall back to factory
      if (prev.costSource === `distributor_${idx}`) {
        newCostSource = 'factory';
      } else if (prev.costSource?.startsWith('distributor_')) {
        // Adjust index if a distributor before the selected one was removed
        const selectedIdx = parseInt(prev.costSource.split('_')[1], 10);
        if (selectedIdx > idx) {
          newCostSource = `distributor_${selectedIdx - 1}`;
        }
      }
      return { ...prev, distributorCosts: newCosts, costSource: newCostSource };
    });
  };

  const toggleTag = (tag) => {
    setForm((prev) => {
      const tags = prev.tags.includes(tag) ? prev.tags.filter((t) => t !== tag) : [...prev.tags, tag];
      return { ...prev, tags };
    });
  };

  const toggleManager = (key) => {
    setOpenManagers((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = () => {
    // 需求 R129 R3：與 ProductDetailPage 同一份必填定義、同一組 PM 決議
    // （Q1=C 七欄／Q2=A 材積看 volumeCbm>0／Q3=A 空白才算未填／Q4=B 紅框紅字不用 alert／
    // Q5=A 只跳第一個）。原本這裡只有 `if (!form.productName.trim()) alert(...)` 一行，
    // 那是「畫面標了 * 卻沒驗」落差的來源，本輪一併收斂。
    // AC13：檢視模式不驗證也不標示（檢視模式本來就不渲染儲存鈕，這裡是第二層保險）。
    if (isView) return;
    const missing = findMissingRequiredFields(form);
    if (missing.length > 0) {
      setMissingKeys(missing.map((f) => f.key));
      jumpToRequiredField(missing[0].key);
      return;
    }
    setMissingKeys([]);

    // Include HK computed values in save
    const saveData = {
      ...form,
      hkReferencePrice: hkValues.hkReferencePrice ?? form.hkReferencePrice,
      hkShippingCostPerPack: hkValues.hkShippingCostPerPack ?? form.hkShippingCostPerPack,
      hkShippingCostPerBox: hkValues.hkShippingCostPerBox ?? form.hkShippingCostPerBox,
      hkTaiwanPrice: hkValues.hkTaiwanPrice ?? form.hkTaiwanPrice,
      hkLogisticsCost: hkValues.hkLogisticsCost ?? form.hkLogisticsCost,
      hkShippingFee: hkValues.hkShippingFee ?? form.hkShippingFee,
      hkInboundCost: hkValues.hkInboundCost ?? form.hkInboundCost,
      hkExcluded: form.hkExcluded,
    };

    onSave(saveData);
  };

  const handleDelete = () => {
    if (!product) return;
    if (window.confirm(`確定要刪除「${product.productName}」嗎？此操作無法復原。`)) {
      onDelete(product._id);
    }
  };

  const enableEdit = () => { setCurrentMode('edit'); onModeChange('edit'); };

  const cancelEdit = () => {
    if (product) {
      setCurrentMode('view');
      onModeChange('view');
      if (fullProduct) populateForm(fullProduct);
    } else {
      onClose();
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('zh-TW', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  const formatDateTime = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleString('zh-TW', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  if (!open) return null;

  const title = currentMode === 'add' ? '📦 新增產品' : currentMode === 'view' ? '📦 產品詳情' : '✏️ 編輯產品';

  // Resolve actual tab index from visible tabs
  const activeTabLabel = visibleTabs[activeTab]?.label;

  // Tab renderers

  const renderBasicInfo = () => {
    const disabled = isView || !canBasicEdit;
    return (
      <div>
        {/* Row 1: 系統辨識碼, 產品編號, 產品名稱, 英文品名 */}
        <div className="product-section-header">基本資料</div>
        <div className="product-form-grid product-form-grid-4" style={{ marginBottom: 'var(--spacing-md)' }}>
          <div className="form-group">
            <label>系統辨識碼</label>
            <input type="text" className="form-control" value={form.systemCode} disabled readOnly placeholder="建立時自動產生" />
          </div>
          <div className="form-group">
            <label>產品編號</label>
            <input type="text" className="form-control" value={form.productCode} onChange={(e) => updateField('productCode', e.target.value)} disabled={disabled} placeholder="非必填，例如：FQ-001" />
          </div>
          <div className="form-group" ref={registerRequiredBox('productName')}>
            <label>產品名稱 <span style={{ color: 'var(--color-danger)' }}>*</span></label>
            <input ref={registerRequiredInput('productName')} type="text" className={requiredClass('productName')} value={form.productName} onChange={(e) => updateField('productName', e.target.value)} disabled={disabled} placeholder="請輸入產品名稱" />
            {renderRequiredError('productName')}
          </div>
          <div className="form-group">
            <label>英文品名</label>
            <input type="text" className="form-control" value={form.englishName} onChange={(e) => updateField('englishName', e.target.value)} disabled={disabled} placeholder="English Name" />
          </div>
        </div>

        {/* Row 2: Series, Category */}
        <div className="product-form-grid product-form-grid-3" style={{ marginBottom: 'var(--spacing-md)' }}>
          {[
            { key: 'series', label: '系列', opts: seriesOptions, setOpts: setSeriesOptions, type: 'series' },
            { key: 'category', label: '類別', opts: categoryOptions, setOpts: setCategoryOptions, type: 'category' },
          ].map(({ key, label, opts, setOpts, type }) => (
            <div key={key} className="form-group">
              <label>{label}</label>
              <div className="product-dropdown-row">
                <select
                  className="form-control"
                  value={form[key]}
                  onChange={(e) => updateField(key, e.target.value)}
                  disabled={disabled}
                >
                  <option value="">— 請選擇 —</option>
                  {opts.map((o) => <option key={o._id} value={o.value}>{o.value}</option>)}
                </select>
                {!disabled && (
                  <button type="button" className="product-dropdown-manage-btn" onClick={() => toggleManager(key)} title="管理選項">⚙</button>
                )}
              </div>
              {!disabled && openManagers[key] && (
                <OptionManager
                  type={type}
                  label={label}
                  options={opts}
                  onOptionsChange={setOpts}
                  canEdit={canBasicEdit}
                />
              )}
            </div>
          ))}
        </div>

        {/* Row 3: Vegetarian — 需求 R59 D3(A) / D6(B) 純文字自由輸入（推翻 需求 R34 拍板）。
            PM 2026-07-04 拍板 D1=C / D3=A / D6=B：移除 select、OptionManager
            齒輪與 R3 disabled option inject，避免 UI/匯出 K 欄不一致。ProductOption(type=
            vegetarianType) 歷史資料仍保留於 DB（D1=C），僅前端管理入口停用。 */}
        <div className="product-form-grid product-form-grid-4" style={{ marginBottom: 'var(--spacing-md)' }}>
          <div className="form-group">
            <label>葷素備註</label>
            <input
              type="text"
              className="form-control"
              value={form.vegetarianType}
              onChange={(e) => updateField('vegetarianType', e.target.value)}
              disabled={disabled}
              placeholder="葷素備註（自由輸入）"
            />
          </div>
        </div>

        {/* Row 4: Spec, PcsPerBox, Barcode */}
        <div className="product-form-grid product-form-grid-4" style={{ marginBottom: 'var(--spacing-md)' }}>
          {/* 需求 R129 Q1=C：規格／箱入數／保存期限本次列入必填，補上 * 記號（原本這三欄連 * 都沒有）。 */}
          <div className="form-group" ref={registerRequiredBox('spec')}>
            <label>規格/容量 <span style={{ color: 'var(--color-danger)' }}>*</span></label>
            <input ref={registerRequiredInput('spec')} type="text" className={requiredClass('spec')} value={form.spec} onChange={(e) => updateField('spec', e.target.value)} disabled={disabled} placeholder="如：500g" />
            {renderRequiredError('spec')}
          </div>
          <div className="form-group" ref={registerRequiredBox('pcsPerBox')}>
            <label>箱入數 <span style={{ color: 'var(--color-danger)' }}>*</span> <span style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>(公式依賴)</span></label>
            <input ref={registerRequiredInput('pcsPerBox')} type="text" inputMode="numeric" className={requiredClass('pcsPerBox')} value={form.pcsPerBox} onChange={(e) => { const v = e.target.value; if (v === '' || /^\d*$/.test(v)) updateField('pcsPerBox', v); }} disabled={disabled} placeholder="如：24" />
            {renderRequiredError('pcsPerBox')}
          </div>
          <div className="form-group">
            <label>條碼</label>
            <input type="text" className="form-control" value={form.barcode} onChange={(e) => updateField('barcode', e.target.value)} disabled={disabled} placeholder="條碼" />
          </div>
        </div>

        {/* Row 5: ShelfLife, RetailPrice, SealingColor, hkExcluded */}
        <div className="product-form-grid product-form-grid-4" style={{ marginBottom: 'var(--spacing-md)' }}>
          <div className="form-group" ref={registerRequiredBox('shelfLife')}>
            <label>保存期限 <span style={{ color: 'var(--color-danger)' }}>*</span></label>
            <input ref={registerRequiredInput('shelfLife')} type="text" className={requiredClass('shelfLife')} value={form.shelfLife} onChange={(e) => updateField('shelfLife', e.target.value)} disabled={disabled} placeholder="如：12個月" />
            {renderRequiredError('shelfLife')}
          </div>
          <div className="form-group">
            <label>末端價</label>
            <input type="text" inputMode="decimal" className="form-control" value={form.retailPrice} onChange={(e) => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) updateField('retailPrice', v); }} disabled={disabled} placeholder="0.0" onBlur={(e) => { if (e.target.value) updateField('retailPrice', fmtMoney(e.target.value)); }} />
          </div>
          <div className="form-group">
            <label>封箱色</label>
            <input type="text" className="form-control" value={form.sealingColor} onChange={(e) => updateField('sealingColor', e.target.value)} disabled={disabled} placeholder="如：紅色" />
          </div>
          <div className="form-group">
            <label>業務備註</label>
            <input type="text" className="form-control" value={form.salesNotes} onChange={(e) => updateField('salesNotes', e.target.value)} disabled={disabled} placeholder="業務備註" />
          </div>
          <div className="form-group">
            <label>香港不出</label>
            <div style={{ paddingTop: 'var(--spacing-sm)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)', cursor: disabled ? 'default' : 'pointer' }}>
                <input type="checkbox" checked={form.hkExcluded} onChange={(e) => updateField('hkExcluded', e.target.checked)} disabled={disabled} />
                <span>不出貨至香港</span>
              </label>
            </div>
          </div>
        </div>

        {/* Tags */}
        <div className="product-section-header">產品標籤</div>
        <div className="form-group" style={{ marginBottom: 'var(--spacing-md)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-xs)' }}>
            <label style={{ margin: 0 }}>標籤選取</label>
            {!disabled && (
              <button type="button" className="product-dropdown-manage-btn" onClick={() => toggleManager('tags')}>⚙ 管理標籤選項</button>
            )}
          </div>
          {/* Selected chips */}
          {form.tags.length > 0 && (
            <div className="product-tag-chips" style={{ marginBottom: 'var(--spacing-xs)' }}>
              {form.tags.map((t) => (
                <span key={t} className="product-tag-chip">
                  {t}
                  {!disabled && (
                    <button type="button" className="product-tag-chip-remove" onClick={() => toggleTag(t)}>✕</button>
                  )}
                </span>
              ))}
            </div>
          )}
          {/* 需求 R28（PM 2026-05-21）：業務(sales) 無 PRODUCT_BASIC_EDIT，
              當 disabled === true 時下方可選 tag 列表會被隱藏，若該產品 form.tags 為空就只剩
              「產品標籤」標題、整個區塊完全空白。補上唯讀的空狀態提示，避免 PM 截圖所述
              「畫面中標籤的資訊僅呈現標題、無內容」。業務仍無法新增/移除/管理 tag。 */}
          {disabled && form.tags.length === 0 && (
            <div style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)', padding: 'var(--spacing-xs) 0' }}>
              尚未設定標籤
            </div>
          )}
          {/* Tag panel */}
          {!disabled && tagOptions.length > 0 && (
            <div className="product-tags-panel">
              <div className="product-tags-grid">
                {tagOptions.map((t) => (
                  <div
                    key={t._id}
                    className={`product-tag-option${form.tags.includes(t.value) ? ' selected' : ''}`}
                    onClick={() => toggleTag(t.value)}
                  >
                    {t.value}
                  </div>
                ))}
              </div>
            </div>
          )}
          {!disabled && openManagers['tags'] && (
            <OptionManager
              type="tag"
              label="標籤"
              options={tagOptions}
              onOptionsChange={setTagOptions}
              canEdit={canBasicEdit}
            />
          )}
        </div>

        {/* Product Image */}
        <div className="product-section-header">產品圖片</div>
        <div className="form-group">
          <label>產品圖片 <span style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>(jpg/png/webp, 自動壓縮至 300KB 以下)</span></label>
          {!disabled && (
            <input
              type="file"
              accept=".jpg,.jpeg,.png,.webp"
              className="form-control"
              style={{ padding: '6px' }}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                // 需求 R89（PM 2026-07-31 拍板）：移除前端 5MB 阻擋，
                // 讓大檔也能送到後端，由後端 sharp 壓縮至 300KB 後存檔。
                const formData = new FormData();
                formData.append('image', file);
                try {
                  const token = localStorage.getItem('token');
                  const res = await fetch(`${API}/products/upload-image`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` },
                    body: formData,
                  });
                  if (res.ok) {
                    const { url } = await res.json();
                    updateField('productImage', url);
                  } else {
                    // 需求 R89：後端超限／格式錯誤會回 JSON { message }。但移除前端 5MB 阻擋後，
                    // 大檔也可能先被反向代理／平台擋下並回「非 JSON」的 413 頁面，此時 res.json()
                    // 會丟出例外、落到下方 catch，使用者只看得到通用「上傳失敗」，AC12 要的可讀
                    // 訊息在最需要的大檔情境反而失效。比照其餘 5 個上傳入口加上 .catch fallback。
                    const err = await res.json().catch(() => ({}));
                    alert(err.message || (res.status === 413 ? '圖片檔案過大，請勿超過 50MB' : '上傳失敗'));
                  }
                } catch (err) {
                  console.error('上傳失敗', err);
                  alert('上傳失敗');
                }
                e.target.value = '';
              }}
            />
          )}
          {/* 需求 R86：追加螢幕截圖上傳（保留上方檔案上傳）。桌面支援 getDisplayMedia 時
              才會出現按鈕；截取後可於預覽自行框選要上傳的範圍（回答 PM D2）。手機／不支援
              的瀏覽器 ScreenshotCapture 回傳 null，沿用檔案上傳。 */}
          {!disabled && (
            <ScreenshotCapture onUploaded={(url) => updateField('productImage', url)} />
          )}
          {form.productImage && (
            <div style={{ marginTop: 8, position: 'relative', display: 'inline-block' }}>
              <img src={form.productImage} alt="product" style={{ maxWidth: 160, maxHeight: 160, borderRadius: 6, border: '1px solid var(--color-border-light)' }} onError={(e) => { e.target.style.display = 'none'; }} />
              {!disabled && (
                <button
                  type="button"
                  style={{ position: 'absolute', top: -6, right: -6, background: 'var(--color-danger)', color: '#fff', border: 'none', borderRadius: '50%', width: 22, height: 22, fontSize: 12, cursor: 'pointer', lineHeight: '20px', textAlign: 'center' }}
                  onClick={() => updateField('productImage', '')}
                  title="移除圖片"
                >✕</button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderLogistics = () => {
    const disabled = isView || !canLogisticsEdit;
    return (
      <div>
        <div className="product-section-header">重量</div>
        <div className="product-form-grid product-form-grid-4" style={{ marginBottom: 'var(--spacing-lg)' }}>
          <div className="form-group" ref={registerRequiredBox('netWeight')}>
            <label>淨重 (kg) <span style={{ color: 'var(--color-danger)' }}>*</span></label>
            <input ref={registerRequiredInput('netWeight')} type="text" inputMode="decimal" className={requiredClass('netWeight')} value={form.netWeight} onChange={(e) => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) updateField('netWeight', v); }} disabled={disabled} placeholder="淨重" />
            {renderRequiredError('netWeight')}
          </div>
          <div className="form-group" ref={registerRequiredBox('grossWeight')}>
            <label>毛重 (kg) <span style={{ color: 'var(--color-danger)' }}>*</span></label>
            <input ref={registerRequiredInput('grossWeight')} type="text" inputMode="decimal" className={requiredClass('grossWeight')} value={form.grossWeight} onChange={(e) => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) updateField('grossWeight', v); }} disabled={disabled} placeholder="毛重" />
            {renderRequiredError('grossWeight')}
          </div>
        </div>

        {/* 需求 R129 Q2=A：材積以最終算出的 volumeCbm > 0 判定有填，三種 volumeMethod 皆同準則。
            捲動定位的容器從「外箱尺寸」一路包到 CBM——因為 auto 模式要補的是長寬高（在外箱尺寸區），
            supplier/estimate 模式要補的是 CUFT，只捲到 CBM 那格使用者會看不到真正該填的欄位。 */}
        <div ref={registerRequiredBox('volumeCbm')}>
        <div className="product-section-header">外箱尺寸 (cm)</div>
        <div className="product-form-grid product-form-grid-4" style={{ marginBottom: 'var(--spacing-lg)' }}>
          <div className="form-group">
            <label>外箱長 (L)</label>
            <input ref={form.volumeMethod === 'auto' ? registerRequiredInput('volumeCbm') : undefined} type="text" inputMode="decimal" className="form-control" value={form.boxLength} onChange={(e) => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) updateField('boxLength', v); }} disabled={disabled} placeholder="cm" />
          </div>
          <div className="form-group">
            <label>外箱寬 (W)</label>
            <input type="text" inputMode="decimal" className="form-control" value={form.boxWidth} onChange={(e) => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) updateField('boxWidth', v); }} disabled={disabled} placeholder="cm" />
          </div>
          <div className="form-group">
            <label>外箱高 (H)</label>
            <input type="text" inputMode="decimal" className="form-control" value={form.boxHeight} onChange={(e) => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) updateField('boxHeight', v); }} disabled={disabled} placeholder="cm" />
          </div>
        </div>

        <div className="product-section-header">材積計算</div>
        {!disabled && (
          <div className="volume-method-group" style={{ marginBottom: 'var(--spacing-md)' }}>
            {[
              { value: 'auto', label: '自動計算 (L×W×H÷28317)' },
              { value: 'supplier', label: '供應商提供' },
              { value: 'estimate', label: '估算' },
            ].map((m) => (
              <label key={m.value} className="volume-method-option">
                <input type="radio" name="volumeMethod" value={m.value} checked={form.volumeMethod === m.value} onChange={(e) => updateField('volumeMethod', e.target.value)} />
                {m.label}
              </label>
            ))}
          </div>
        )}
        {disabled && (
          <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-md)' }}>
            計算方式：{form.volumeMethod === 'auto' ? '自動計算' : form.volumeMethod === 'supplier' ? '供應商提供' : '估算'}
          </div>
        )}
        <div className="product-form-grid product-form-grid-3">
          <div className="form-group">
            <label>CUFT (立方英尺)</label>
            <input
              ref={form.volumeMethod === 'auto' ? undefined : registerRequiredInput('volumeCbm')}
              type="text"
              inputMode="decimal"
              className={`form-control${form.volumeMethod === 'auto' ? ' input-computed' : ''}`}
              value={form.volumeCuft}
              onChange={(e) => {
                if (form.volumeMethod === 'auto') return;
                const v = e.target.value;
                if (v === '' || /^\d*\.?\d*$/.test(v)) updateField('volumeCuft', v);
              }}
              disabled={disabled || form.volumeMethod === 'auto'}
              placeholder="CUFT"
            />
            {form.volumeMethod === 'auto' && <small style={{ color: 'var(--color-info-dark)', fontSize: '11px' }}>自動計算</small>}
          </div>
          <div className="form-group">
            <label>CBM (立方公尺) <span style={{ color: 'var(--color-danger)' }}>*</span></label>
            <input
              type="text"
              inputMode="decimal"
              className={requiredClass('volumeCbm', 'form-control input-computed')}
              value={form.volumeCbm}
              disabled
              placeholder="CBM"
            />
            <small style={{ color: 'var(--color-info-dark)', fontSize: '11px' }}>自動由 CUFT 計算</small>
            {renderRequiredError('volumeCbm')}
          </div>
        </div>
        </div>
      </div>
    );
  };

  const renderCost = () => {
    const disabled = isView || !canBasicEdit;

    const renderCostEntry = (entry, onChange, label, onRemove, costSourceValue) => (
      <div className="cost-card" style={{ marginBottom: 'var(--spacing-md)', border: form.costSource === costSourceValue ? '2px solid var(--color-primary)' : undefined }}>
        <div className="cost-card-header">
          <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
            {!disabled && (
              <input
                type="radio"
                name="costSourceInCard"
                checked={form.costSource === costSourceValue}
                onChange={() => updateField('costSource', costSourceValue)}
                style={{ marginRight: 4 }}
              />
            )}
            {label}
            {form.costSource === costSourceValue && (
              <span style={{ fontSize: '11px', background: 'var(--color-primary)', color: '#fff', padding: '1px 8px', borderRadius: 10 }}>使用中</span>
            )}
          </span>
          {onRemove && !disabled && (
            <button className="cost-remove-btn" onClick={onRemove}>移除</button>
          )}
        </div>
        <div className="cost-card-body">
          <div className="product-form-grid product-form-grid-4" style={{ marginBottom: 'var(--spacing-sm)' }}>
            <div className="form-group">
              <label>供應商名稱</label>
              <input type="text" className="form-control" value={entry.supplierName} onChange={(e) => onChange('supplierName', e.target.value)} disabled={disabled} placeholder="供應商名稱" />
            </div>
            <div className="form-group">
              <label>成本金額(箱價)</label>
              <input
                type="text"
                inputMode="decimal"
                className="form-control"
                value={entry.boxPrice}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '' || /^\d*\.?\d*$/.test(v)) onChange('boxPrice', v);
                }}
                disabled={disabled}
                placeholder="0.0"
                onBlur={(e) => { if (e.target.value) onChange('boxPrice', fmtMoney(e.target.value)); }}
              />
            </div>
            <div className="form-group">
              <label>成本金額(單價) <span style={{ fontSize: '11px', color: 'var(--color-info-dark)' }}>自動</span></label>
              <input
                type="text"
                className="form-control input-computed"
                value={fmtMoney(entry.unitPrice)}
                disabled
                placeholder="自動計算"
              />
            </div>
            <div className="form-group">
              <label>MOQ</label>
              <input type="text" className="form-control" value={entry.moq} onChange={(e) => onChange('moq', e.target.value)} disabled={disabled} placeholder="最小訂單量" />
            </div>
          </div>
          {/* 需求 R44（PM 2026-06-01 拍板方案 B）：供應商備註獨立成第二列 full-width textarea，依內容自動拉高。 */}
          <div className="form-group" style={{ marginBottom: 'var(--spacing-sm)' }}>
            <label>供應商備註</label>
            {isView ? (
              <div className="form-control pd-notes-view" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', minHeight: 38 }}>{entry.notes || ' '}</div>
            ) : (
              <textarea
                ref={(el) => {
                  if (el) {
                    el.style.height = 'auto';
                    el.style.height = `${el.scrollHeight}px`;
                  }
                }}
                className="form-control"
                value={entry.notes}
                onChange={(e) => {
                  onChange('notes', e.target.value);
                  e.target.style.height = 'auto';
                  e.target.style.height = `${e.target.scrollHeight}px`;
                }}
                disabled={disabled}
                placeholder="供應商備註"
                rows={1}
                style={{ resize: 'vertical', overflow: 'hidden', minHeight: 38, lineHeight: 1.5 }}
              />
            )}
          </div>
          <div className="product-form-grid product-form-grid-2">
            <div className="form-group">
              <label>可出貨國家</label>
              <input type="text" className="form-control" value={entry.shippableCountries} onChange={(e) => onChange('shippableCountries', e.target.value)} disabled={disabled} placeholder="如：JP, US, EU" />
            </div>
            <div className="form-group">
              <label>不可出貨國家</label>
              <input type="text" className="form-control" value={entry.nonShippableCountries} onChange={(e) => onChange('nonShippableCountries', e.target.value)} disabled={disabled} placeholder="如：CN, KR" />
            </div>
          </div>
        </div>
      </div>
    );

    return (
      <div>
        <div className="product-section-header">工廠成本</div>
        {renderCostEntry(form.factoryCost, (field, val) => updateFactoryCost(field, val), '工廠成本', null, 'factory')}

        <div className="product-section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>經銷成本</span>
          {!disabled && (
            <button
              className="cost-add-btn"
              style={{ width: 'auto', padding: '2px 12px', fontSize: '12px' }}
              onClick={addDistributor}
              disabled={form.distributorCosts.length >= 3}
            >
              + 增加經銷成本 ({form.distributorCosts.length}/3)
            </button>
          )}
        </div>

        {form.distributorCosts.length === 0 && (
          <div style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)', padding: 'var(--spacing-md)', textAlign: 'center', border: '1px dashed var(--color-border)', borderRadius: 'var(--border-radius-md)', marginBottom: 'var(--spacing-md)' }}>
            尚無經銷成本
          </div>
        )}

        {form.distributorCosts.map((d, idx) =>
          renderCostEntry(
            d,
            (field, val) => updateDistributorCost(idx, field, val),
            `經銷商 #${idx + 1}`,
            () => removeDistributor(idx),
            `distributor_${idx}`
          )
        )}
      </div>
    );
  };

  const renderHK = () => {
    const isManager = role === 'manager';
    const isPurchaser = role === 'purchasing';
    const canEditMarkup = isManager || isPurchaser;

    const displayInt = (computed, stored) => {
      const v = computed !== undefined && computed !== '' ? computed : stored;
      if (v === '' || v === null || v === undefined) return '—';
      const n = parseFloat(v);
      if (isNaN(n)) return String(v);
      return n.toFixed(1);
    };
    const displayDec1 = (computed, stored) => {
      const v = computed !== undefined && computed !== '' ? computed : stored;
      if (v === '' || v === null || v === undefined) return '—';
      const n = parseFloat(v);
      if (isNaN(n)) return String(v);
      return n.toFixed(1);
    };

    const fields = [
      { label: '台灣進價(箱)', key: 'hkTaiwanPrice', formula: hkValues.hkTaiwanPrice, stored: form.hkTaiwanPrice, fmt: displayInt },
      { label: '船務成本', key: 'hkShippingFee', formula: hkValues.hkShippingFee, stored: form.hkShippingFee, fmt: displayDec1 },
      { label: '進倉成本', key: 'hkInboundCost', formula: hkValues.hkInboundCost, stored: form.hkInboundCost, fmt: displayDec1 },
      { label: '物流成本', key: 'hkLogisticsCost', formula: hkValues.hkLogisticsCost, stored: form.hkLogisticsCost, fmt: displayDec1 },
      { label: '出貨成本(箱)', key: 'hkShippingCostPerBox', formula: hkValues.hkShippingCostPerBox, stored: form.hkShippingCostPerBox, fmt: displayInt },
      { label: '出貨成本(包)', key: 'hkShippingCostPerPack', formula: hkValues.hkShippingCostPerPack, stored: form.hkShippingCostPerPack, fmt: displayInt },
      { label: '參考價格', key: 'hkReferencePrice', formula: hkValues.hkReferencePrice, stored: form.hkReferencePrice, fmt: displayInt },
    ];

    return (
      <div>
        {hkdRate && (
          <div style={{ background: 'var(--color-background)', border: '1px solid var(--color-border-light)', borderRadius: 'var(--border-radius-sm)', padding: 'var(--spacing-sm) var(--spacing-md)', marginBottom: 'var(--spacing-md)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
            使用匯率：1 HKD = {hkdRate} TWD (香港內部匯率)
          </div>
        )}

        <div style={{ marginBottom: 'var(--spacing-md)' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)' }}>
            <input
              type="checkbox"
              checked={form.hkMarkup}
              onChange={(e) => canEditMarkup && !isView && updateField('hkMarkup', e.target.checked)}
              disabled={!canEditMarkup || isView}
            />
            <span>勾選後此產品加價 5%（主管／採購）</span>
          </label>
        </div>

        <div className="product-section-header">香港專用欄位（自動計算）</div>
        <div className="hk-field-row">
          {fields.map(({ label, formula, stored, fmt }) => (
            <div key={label} className="form-group hk-computed-field">
              <span className="hk-computed-badge">自動</span>
              <label>{label}</label>
              <input
                type="text"
                className="form-control input-computed"
                value={fmt(formula, stored)}
                disabled
              />
            </div>
          ))}
        </div>

        {(!hkdRate || !form.pcsPerBox || !(form.factoryCost?.boxPrice || form.distributorCosts?.[0]?.boxPrice)) && (
          <div style={{ padding: 'var(--spacing-md)', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 'var(--border-radius-sm)', fontSize: 'var(--font-size-sm)', color: '#856404' }}>
            ⚠️ 請先填寫箱入數、成本金額(箱價)，並確認 HKD 匯率，以顯示計算結果。
          </div>
        )}
      </div>
    );
  };

  const renderMarketing = () => {
    // 需求 R4（PM 2026-05-08 補充指示）：
    // 採購可編輯「產品狀態（上架/下架）」，但目錄/官網狀態仍只有主管可改。
    const productStatusDisabled = isView || !canProductStatusEdit;
    const disabled = isView || !canMarketingEdit;

    return (
      <div>
        <div className="product-section-header">上架狀態</div>
        <div className="form-group" style={{ marginBottom: 'var(--spacing-lg)' }}>
          <label>產品狀態</label>
          <div className="marketing-radio-group">
            {[
              { value: 'listed', label: '🟢 上架' },
              { value: 'delisted', label: '⭕ 下架' },
            ].map((s) => (
              <label
                key={s.value}
                className={`marketing-radio-option${form.productStatus === s.value ? ' selected' : ''}`}
                onClick={() => !productStatusDisabled && updateField('productStatus', s.value)}
              >
                <input type="radio" name="productStatus" value={s.value} checked={form.productStatus === s.value} onChange={() => {}} style={{ pointerEvents: 'none' }} />
                {s.label}
              </label>
            ))}
          </div>
        </div>

        <div className="product-section-header">目錄狀態</div>
        <div className="form-group">
          <label>目錄/官網狀態</label>
          <div className="marketing-radio-group">
            <label
              className={`marketing-radio-option${form.catalogStatus === 'in_catalog' ? ' selected' : ''}`}
              onClick={() => !disabled && setForm((prev) => ({ ...prev, catalogStatus: 'in_catalog', websiteStatus: '' }))}
            >
              <input type="radio" name="marketingStatus" value="in_catalog" checked={form.catalogStatus === 'in_catalog'} onChange={() => {}} style={{ pointerEvents: 'none' }} />
              📋 上目錄
            </label>
            {[
              { value: 'pending_website', label: '🔄 要上官網' },
              { value: 'on_website', label: '🌐 已上官網' },
              // 需求 R128（PM 2026-08-21）：三選一 → 四選一；「不上行銷」改回「不上官網」（值不動）。
              // 單選機制不變：仍由下方 onClick 一次寫 websiteStatus 單值並清空 catalogStatus。
              { value: 'not_on_website', label: '🚫 不上官網' },
              { value: 'delist_website', label: '📉 官網需下架' },
            ].map((s) => (
              <label
                key={s.value}
                className={`marketing-radio-option${form.websiteStatus === s.value ? ' selected' : ''}`}
                onClick={() => !disabled && setForm((prev) => ({ ...prev, websiteStatus: s.value, catalogStatus: '' }))}
              >
                <input type="radio" name="marketingStatus" value={s.value} checked={form.websiteStatus === s.value} onChange={() => {}} style={{ pointerEvents: 'none' }} />
                {s.label}
              </label>
            ))}
          </div>
        </div>

        {disabled && !isView && (
          <div style={{ marginTop: 'var(--spacing-md)', padding: 'var(--spacing-sm)', background: 'var(--color-background)', borderRadius: 'var(--border-radius-sm)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border-light)' }}>
            📝 僅主管可編輯目錄/官網狀態
          </div>
        )}
      </div>
    );
  };

  const renderAudit = () => {
    const allLog = fullProduct?.auditLog || [];
    // 需求 R5（PM 2026-05-08 後續追加要求）：
    // 稽核紀錄需顯示「工廠成本(箱價)」、「經銷成本(箱價)」與「採購成本(單價)」。
    //
    // 需求 R146（PM 2026-08-29「本案可結案，僅是盤點現況」＝Q1=A）：盤點後維持現況，
    // 顯示範圍仍是這 3 類，缺口（costSource／香港區塊／末端價／報價單端）不在本輪補。
    //
    // 需求 R164（PM 2026-09-07「若材積變動，成本就會變動，這樣『＄』符號也要顯示」）：
    // 追蹤／顯示範圍由 3 類擴張為 4 類，新增「材積(CUFT)」（只追 volumeCuft 一個值，
    // volumeCbm 與箱規長寬高都是它的換算來源／結果，詳見 ProductDetailPage.renderAudit 註解）。
    //
    // 條件與 ProductDetailPage.js、scripts/cleanAuditLog.js、utils/sensitivePriceAlert.js、
    // server utils/sensitivePriceAudit.js 五處必須逐字一致
    // （tests/priceAuditScope.fixture.js／tests/volumeCostAudit.fixture.js 會比對）。
    const log = allLog.filter((entry) => {
      const field = entry.field || '';
      const isCostBox = (field.startsWith('工廠成本') || field.startsWith('經銷成本')) && field.endsWith('(箱價)');
      const isPurchaseUnit = field === '採購成本(單價)';
      const isVolume = field === '材積(CUFT)';
      return isCostBox || isPurchaseUnit || isVolume;
    });
    return (
      <div>
        <div className="product-section-header">稽核紀錄</div>
        {log.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--color-text-secondary)' }}>尚無變動紀錄</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="audit-table">
              <thead>
                <tr>
                  <th>變動日期</th>
                  <th>欄位</th>
                  <th>原始值</th>
                  <th>新值</th>
                  <th>操作人</th>
                </tr>
              </thead>
              <tbody>
                {[...log].reverse().map((entry) => (
                  <tr key={entry._id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(entry.changedAt)}</td>
                    <td>{entry.field}</td>
                    <td>{entry.oldValue}</td>
                    <td>{entry.newValue}</td>
                    <td>{entry.changedBy?.name || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTabLabel) {
      case '產品基本資訊': return renderBasicInfo();
      case '物流與物理屬性': return renderLogistics();
      case '成本與階梯價格': return renderCost();
      case '香港專用欄位': return renderHK();
      case '行銷': return renderMarketing();
      case '稽核紀錄': return renderAudit();
      default: return null;
    }
  };

  return (
    <div className={`product-modal-overlay${open ? ' open' : ''}`} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="product-modal">
        {/* Header */}
        <div className="modal-header">
          <div>
            <h2 className="modal-title">{title}</h2>
            {(currentMode === 'view' || currentMode === 'edit') && (form.productName || form.spec || form.pcsPerBox) && (
              <span className="product-code" style={{ display: 'block', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginTop: '2px' }}>
                {[form.productName, form.spec ? `${form.spec}${form.packingUnit ? `/${form.packingUnit}` : ''}` : '', form.pcsPerBox ? `箱入數: ${form.pcsPerBox}` : ''].filter(Boolean).join(' / ')}
              </span>
            )}
          </div>
          <button className="modal-close" onClick={onClose} title="關閉">✕</button>
        </div>

        {/* Meta info */}
        {product && (
          <div style={{ display: 'flex', gap: 'var(--spacing-lg)', padding: 'var(--spacing-sm) var(--spacing-lg)', background: 'var(--color-background)', borderBottom: '1px solid var(--color-border-light)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', flexShrink: 0 }}>
            <span>📅 建檔：{formatDate(product.createdAt)}</span>
            {fullProduct?.createdBy && <span>👤 建立人：{fullProduct.createdBy.name}</span>}
            <span>編號：<strong style={{ color: 'var(--color-text-primary)', fontFamily: 'monospace' }}>{product.productCode}</strong></span>
          </div>
        )}

        {/* Tabs */}
        <div className="product-tabs">
          {visibleTabs.map((tab, idx) => (
            <button
              key={tab.label}
              className={`product-tab-btn${activeTab === idx ? ' active' : ''}`}
              onClick={() => setActiveTab(idx)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className={`modal-body${isView ? ' product-view-mode' : ''}`}>
          {renderTabContent()}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <div>
            {currentMode === 'edit' && product && canBasicEdit && (
              <button className="btn btn-danger" onClick={handleDelete}>🗑️ 刪除此產品</button>
            )}
          </div>
          <div className="modal-footer-right">
            {isView ? (
              <>
                <button className="btn btn-outline" onClick={onClose}>關閉</button>
                {canBasicEdit && (
                  <button className="btn btn-primary" onClick={enableEdit}>✏️ 編輯</button>
                )}
              </>
            ) : (
              <>
                <button className="btn btn-outline" onClick={cancelEdit}>取消</button>
                <button className="btn btn-primary" onClick={handleSave}>✓ 儲存</button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductModal;
